'use client';

import { useState, useEffect, useCallback, useRef, useReducer } from 'react';
import Link from 'next/link';

type SearchResult = {
  title: string;
  slug: string;
  category: string;
  imagePath: string | null;
};

const categoryRoute: Record<string, string> = {
  CARTOON: 'karikaturler',
  JOKE: 'fikralar',
  STORY: 'hikayeler',
};

const categoryEmoji: Record<string, string> = {
  CARTOON: '\u{1F588}\u{FE0F}',
  JOKE: '\u{1F604}',
  STORY: '\u{1F4DA}',
};

const categoryLabel: Record<string, string> = {
  CARTOON: 'Karikatür',
  JOKE: 'Fıkra',
  STORY: 'Hikâye',
};

type SearchState = {
  results: SearchResult[];
  loading: boolean;
  error: boolean;
};

type SearchAction =
  | { type: 'reset' }
  | { type: 'start' }
  | { type: 'success'; results: SearchResult[] }
  | { type: 'fail' };

function searchReducer(state: SearchState, action: SearchAction): SearchState {
  switch (action.type) {
    case 'reset':
      return { results: [], loading: false, error: false };
    case 'start':
      return { ...state, loading: true, error: false };
    case 'success':
      return { results: action.results, loading: false, error: false };
    case 'fail':
      return { ...state, loading: false, error: true };
  }
}

type GlobalSearchProps = {
  open: boolean;
  onClose: () => void;
};

export default function GlobalSearch({ open, onClose }: GlobalSearchProps) {
  const [{ results, loading, error }, dispatch] = useReducer(searchReducer, {
    results: [],
    loading: false,
    error: false,
  });
  const [query, setQuery] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();

    const trimmed = query.trim();
    if (trimmed.length < 2) {
      dispatch({ type: 'reset' });
      return;
    }

    dispatch({ type: 'start' });

    const controller = new AbortController();
    abortRef.current = controller;

    timerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(trimmed)}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        if (!controller.signal.aborted) {
          dispatch({ type: 'success', results: data.results ?? [] });
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (!controller.signal.aborted) {
          dispatch({ type: 'fail' });
        }
      }
    }, 300);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [query]);

  const handleResultClick = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!open) return null;

  const trimmed = query.trim();

  return (
    <div className="absolute left-0 right-0 top-full z-40 border-b border-stone-200 bg-white shadow-lg">
      <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-6 lg:px-8">
        <div className="relative">
          <svg
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-stone-400"
            xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="İçeriklerde ara..."
            aria-label="İçeriklerde ara"
            autoFocus
            className="w-full rounded-2xl border border-stone-200 bg-stone-50 py-3 pl-11 pr-12 text-sm text-stone-900 outline-none transition-all duration-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
          />
          <button
            onClick={onClose}
            aria-label="Aramayı kapat"
            className="absolute right-3 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {loading && (
          <p className="mt-3 text-center text-xs text-stone-400">Aranıyor...</p>
        )}

        {error && (
          <p className="mt-3 text-center text-xs text-red-400">Arama yapılırken bir sorun oluştu.</p>
        )}

        {!loading && !error && trimmed.length >= 2 && (
          <div className="mt-3 max-h-[320px] overflow-y-auto rounded-2xl border border-stone-100 bg-white">
            {results.length > 0 ? (
              <ul className="divide-y divide-stone-100">
                {results.map((item, i) => (
                  <li key={`${item.slug}-${i}`}>
                    <Link
                      href={`/${categoryRoute[item.category] ?? '#'}/${item.slug}`}
                      onClick={handleResultClick}
                      className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-stone-50"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-stone-100 text-sm">
                        {categoryEmoji[item.category] ?? '\u{1F4AC}'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-stone-900">
                          {item.title}
                        </p>
                        <p className="text-xs text-stone-500">{categoryLabel[item.category] ?? item.category}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-4 py-6 text-center text-sm text-stone-500">
                Aradığın içerik henüz buralarda yok.
              </p>
            )}
          </div>
        )}

        {trimmed.length === 0 && (
          <p className="mt-3 text-xs text-stone-400 text-center">
            Karikatür, fıkra ve hikâyelerde ara...
          </p>
        )}
      </div>
    </div>
  );
}
